/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PivotTableUI from 'react-pivottable/PivotTableUI';
import TableRenderers from 'react-pivottable/TableRenderers';
import 'react-pivottable/pivottable.css';
import TableService from '../../../services/TableService';
import { DEFAULT_PIVOT_STATE, mapRowsForPivot } from './pivotData';

const PIVOT_MAX_ROWS = 10000;

const PivotExplorerDialog = ({ open, onClose, apiFilter, refreshKey }) => {
  const [pivotState, setPivotState] = useState(DEFAULT_PIVOT_STATE);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [truncated, setTruncated] = useState(false);

  const fetchPivotRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    setTruncated(false);
    try {
      const response = await TableService.request(
        'reports/digitacao/pivot-rows',
        apiFilter,
      );
      const rawRows = response?.data?.rows;
      const rows = Array.isArray(rawRows) ? rawRows : [];
      setData(mapRowsForPivot(rows));
      const total = response?.data?.totalRows;
      setTruncated(
        response?.data?.truncated === true
          || (typeof total === 'number' && total > rows.length)
          || rows.length >= PIVOT_MAX_ROWS,
      );
    } catch {
      setData([]);
      setError('Dados indisponíveis. O endpoint de pivot ainda não está disponível no servidor.');
    } finally {
      setLoading(false);
    }
  }, [apiFilter, refreshKey]);

  useEffect(() => {
    if (open) {
      setPivotState(DEFAULT_PIVOT_STATE);
      fetchPivotRows();
    }
  }, [open, fetchPivotRows]);

  const hasData = data.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            Explorar dados
          </Typography>
          <Typography variant="body2" color="text.secondary" display="block">
            Arraste campos para linhas, colunas e valores — estilo planilha dinâmica
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Fechar" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {truncated && !loading && hasData && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Exibindo até {PIVOT_MAX_ROWS.toLocaleString('pt-BR')} registros. Refine os
            filtros para análises mais precisas.
          </Alert>
        )}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 280,
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Carregando dados para análise…
            </Typography>
          </Box>
        )}
        {!loading && error && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 280,
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              {error}
            </Typography>
          </Box>
        )}
        {!loading && !error && !hasData && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 280,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Sem dados no período selecionado
            </Typography>
          </Box>
        )}
        {!loading && !error && hasData && (
          <Box
            className="relatorio-digitacao-pivot"
            sx={{
              maxHeight: '70vh',
              overflow: 'auto',
              bgcolor: 'background.paper',
              color: 'text.primary',
              fontFamily: 'typography.fontFamily',
              '& .pvtUi': {
                color: 'inherit',
                fontFamily: 'inherit',
              },
              '& .pvtTable': {
                fontSize: '0.875rem',
              },
              '& .pvtAxisContainer li span.pvtAttr': {
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[200],
                borderColor: 'divider',
              },
            }}
          >
            <PivotTableUI
              data={data}
              onChange={setPivotState}
              renderers={TableRenderers}
              {...pivotState}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

PivotExplorerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  apiFilter: PropTypes.object.isRequired,
  refreshKey: PropTypes.bool,
};

PivotExplorerDialog.defaultProps = {
  refreshKey: false,
};

export default PivotExplorerDialog;
